import { EventEmitter, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { GlobalLoadingService } from '../../global-services/global-loading.service';
import { FormService } from '../../global-services/form.service';
import { CreateFormModel, UpdateFormModel, FormModel, FORM_STATUSES, FormStatus } from '../../global-data-models/form.model';
import { GlobalMessageService } from '../../global-services/global-message.service';
import { v4 as uuidv4 } from 'uuid';
import { ActivatedRoute } from '@angular/router';

export enum CrudFormMode {
  CREATE,
  READ,
  UPDATE,
}
export const DEFAULT_CRUD_FORM_MODE = CrudFormMode.CREATE;

export interface CrudFormUiState {
  form: FormGroup;
  crudMode: CrudFormMode;
  statuses: FormStatus[];
}

@Injectable({ providedIn: 'root' })
export class CrudFormFacade {
  public uiState: CrudFormUiState;

  private uiStateEvent: EventEmitter<CrudFormUiState> = new EventEmitter<CrudFormUiState>();
  public uiState$: Observable<CrudFormUiState> = this.uiStateEvent.asObservable();

  private orgId?: string;

  constructor(
    private readonly loadingService: GlobalLoadingService,
    private readonly fb: FormBuilder,
    private readonly formService: FormService,
    private readonly route: ActivatedRoute,
    private readonly messageService: GlobalMessageService,
  ) {
    // Assign a temporary blank ui state
    this.uiState = {
      form: this.createForm(undefined),
      crudMode: DEFAULT_CRUD_FORM_MODE,
      statuses: [...FORM_STATUSES],
    };

    this.uiStateEvent.emit(this.uiState);
  }

  public async begin(_orgId: string, _crudMode: CrudFormMode, formId?: string) {
    try {
      this.loadingService.show();

      this.orgId = _orgId;

      let formData: FormModel | undefined = undefined;

      if (_crudMode == CrudFormMode.READ || _crudMode == CrudFormMode.UPDATE) {
        // If read or update then formId should be present and form data should be fetched
        if (!formId) {
          console.log('Tried to read|update but formId undefined.');
          return;
        }

        formData = await this.formService.getForm(this.orgId!, formId);
      }

      this.uiState = {
        form: this.createForm(formData as FormModel),
        crudMode: _crudMode,
        statuses: ['DRAFT', 'PUBLISHED'],
      };

      // Configure form based on mode
      if (_crudMode === CrudFormMode.READ) {
        this.uiState.form.disable();
      } else if (_crudMode === CrudFormMode.UPDATE) {
        // In update mode, identifier cannot be changed
        this.uiState.form.get('name')?.disable();
        this.uiState.form.get('identifier')?.disable();
      }

      this.uiStateEvent.emit(this.uiState);
    } catch (error: any) {
      this.messageService.showError('Error', error.message);
      throw Error(error);
    } finally {
      this.loadingService.hide();
    }
  }

  public resetForm(): void {
    this.uiState.form.reset();
  }

  public async submit() {
    if (!this.markFormTouchedAndCheckValidity()) {
      this.messageService.showError('Validation Error', 'Please fill in all required fields correctly.');
      return;
    }

    const formModel = this.formToFormModel();

    try {
      this.loadingService.show();

      if (this.uiState.crudMode === CrudFormMode.CREATE) {
        await this.formService.createForm(this.orgId!, formModel as CreateFormModel);
        this.messageService.showSuccess('Success', 'Form created successfully');
      }

      if (this.uiState.crudMode === CrudFormMode.UPDATE) {
        await this.formService.updateForm(this.orgId!, this.uiState.form.get('id')?.getRawValue(), formModel as UpdateFormModel);
        this.messageService.showSuccess('Success', 'Form updated successfully');
      }
    } catch (error: any) {
      this.messageService.showError('Error', 'Failed to save form: ' + error.message);
      throw new Error(error);
    } finally {
      this.loadingService.hide();
    }
  }

  private markFormTouchedAndCheckValidity(): boolean {
    this.uiState.form.markAllAsTouched();
    return this.uiState.form.valid;
  }

  private formToFormModel(): CreateFormModel | UpdateFormModel {
    const formValues = this.uiState.form.getRawValue();

    const formModel: Record<string, any> = {
      name: formValues.name,
      description: formValues.description,
      formData: formValues.formData,
      isActive: formValues.isActive,
      isTemplate: formValues.isTemplate,
      status: formValues.status,
      organisation_id: this.orgId,
    };

    // For create mode, include optional identifier (version is handled by backend)
    if (this.uiState.crudMode === CrudFormMode.CREATE) {
      if (formValues.identifier) {
        formModel['identifier'] = formValues.identifier;
      }
    }

    if (this.uiState.crudMode == CrudFormMode.UPDATE) {
      formModel['id'] = formValues.id;
    }

    return formModel;
  }

  private createForm(formData?: FormModel): FormGroup {
    return this.fb.group({
      id: [{ value: formData?.id || uuidv4(), disabled: true }],
      organisation_id: [{ value: this.orgId!, disabled: true }],
      identifier: [formData?.identifier || '', [Validators.pattern(/^[a-z0-9][a-z0-9-]*[a-z0-9]$|^[a-z0-9]$/)]],
      name: [formData?.name || '', Validators.required],
      description: [formData?.description || ''],
      version: [{ value: formData?.version || 1, disabled: true }],
      formData: [formData?.formData || null],
      isActive: [formData?.isActive || false],
      isTemplate: [formData?.isTemplate || false],
      status: [formData?.status || 'DRAFT', Validators.required],
    });
  }
}
